import React from "react";
import { Navigate, Outlet, Route, useParams } from "react-router-dom";
import { AuthorizedRoute } from "../shared/components/Routers";
import { Roles } from "../account/models/AccountModels";
import TypeGuard from "../shared/components/TypeGuard";
import PermissionRouteGuard from "../shared/components/PermissionRouteGuard";
import ViewerRouteGuard from "../shared/components/ViewerRouteGuard";
import { Modules } from "../core/models/PermissionModels";
import accountContext from "../account/accountContext";
import Search from "./searchAndBuy/components/Search";
import ProductSuppliers from "./productSuppliers/components/ProductSuppliers";
import Orders from "./orders/components/Orders";
import InventoryTable from "./inventory/components/InventoryTable";
import UploadStockItemsFile from "./inventory/components/UploadStockItemsFile";
import EditStockItem from "./inventory/components/EditStockItem";
import MyRfqs from "./myRfqs/components/MyRfqs";
import CreateRfq from "./myRfqs/components/create/CreateRfq";
import RfqFeed from "./rfqFeed/components/RfqFeed";
import CreateOffer from "./sentOffers/components/create/CreateOffer";
import SentOffers from "./sentOffers/components/SentOffers";
import OffersReceived from "./offersReceived/components/OffersReceived";
import SettingsLayout from "./settings/components/SettingsLayout";
import SettingsProfile from "./settings/components/sections/SettingsProfile";
import SettingsNotifications from "./settings/components/sections/SettingsNotifications";
import SettingsTeam from "./settings/components/sections/SettingsTeam";
import SettingsCompany from "./settings/components/sections/SettingsCompany";
import SettingsPricing from "./settings/components/sections/SettingsPricing";
import SettingsService from "./settings/components/sections/SettingsService";
import SettingsShipping from "./settings/components/sections/SettingsShipping";
import SettingsAppearance from "./settings/components/sections/SettingsAppearance";
import Companies from "./companies/components/Companies";
import Company from "./companies/components/Company";
import Machinery from "./machinery/components/Machinery";
import Machine from "./machinery/components/Machine";
import CatalogBrowsePage from "./machinery/components/CatalogBrowsePage";
import CatalogModelDetailPage from "./machinery/components/CatalogModelDetailPage";
import SrDetailPage from "./machinery/components/srDetail/SrDetailPage";
import CreateServiceReportPage from "./machinery/components/CreateServiceReportPage";
import SolveServiceReportPage from "./machinery/components/SolveServiceReportPage";
import { LocationsPage } from "./machinery/components/locations";
import MyServiceRequests from "./servicing/components/MyServiceRequests";
import ViewServiceRequestPage from "./servicing/components/ViewServiceRequestPage";
import RfsDetailPage from "./servicing/components/rfsDetail/RfsDetailPage";
import EditServiceRequestPage from "./servicing/components/EditServiceRequestPage";
import CompareOffersPage from "./servicing/components/CompareOffersPage";
import ServiceBoard from "./serviceBoard/components/ServiceBoard";
import MyOffers from "./serviceOffers/components/MyOffers";
import ActiveWork from "./serviceOffers/components/ActiveWork";
import OfferDetailPage from "./serviceOffers/components/offerDetail/OfferDetailPage";
import EditOfferPage from "./serviceOffers/components/EditOfferPage";
import NotificationHistoryPage from "../shared/notifications/components/NotificationHistoryPage";
import PartnershipsPage from "./partnerships/components/PartnershipsPage";
import ServiceTrackingPage from "./serviceTracking/components/ServiceTrackingPage";
import MidasPage from "./midas/components/MidasPage";
import MidasRouteGuard from "./midas/components/MidasRouteGuard";
import MidasPageV2 from "./midas/components/v2/MidasPageV2";
import ServiceReportsPage from "./operations/components/serviceReports/ServiceReportsPage";
import WorkBoardPage from "./operations/components/workBoard/WorkBoardPage";
import MyTasksPage from "./operations/components/myTasks/MyTasksPage";
import CreateWorkOrderPage from "./operations/components/CreateWorkOrderPage";
import WorkOrderDetailPage from "./operations/components/WorkOrderDetailPage";
import AnalyticsDashboardPage from "./operations/components/analytics/AnalyticsDashboardPage";
import useDefaultRedirect from "../account/hooks/useDefaultRedirect";

/** Guard that blocks Workers from admin Operations sub-sections. Redirects to /operations/my-tasks. */
const OpsManagerGuard = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  if (isWorker) return <Navigate to="/operations/my-tasks" replace />;
  return <Outlet />;
};

/** Guard that blocks Workers from catalog routes. Redirects to /machinery. */
const MachineryManagerGuard = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  if (isWorker) return <Navigate to="/machinery" replace />;
  return <Outlet />;
};

/** Conditional redirect: Admin/Manager → work-board, Worker → my-tasks */
const OpsIndexRedirect = () => {
  const user = accountContext.user;
  const isWorker = user?.role === Roles.WORKER;
  return <Navigate to={isWorker ? "my-tasks" : "work-board"} replace />;
};

/** Redirect /service-requests/:id → /service-board/:id */
const ServiceRequestRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/service-board/${id}`} replace />;
};

/** Redirect /service-requests/:id/offers/compare → /service-board/:id/offers/compare */
const ServiceRequestCompareRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/service-board/${id}/offers/compare`} replace />;
};

const useCustomerRoutes = () => {
  const { defaultRoute } = useDefaultRedirect();

  return (
    <Route path="">
      {/* Viewer role: no web app access — redirect to QR kiosk */}
      <Route element={<ViewerRouteGuard />}>
      {/* Operator-only routes */}
      <Route element={<TypeGuard requiredType="operator" />}>
        <Route element={<PermissionRouteGuard module={Modules.Machinery} />}>
          <Route path="machinery" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route element={<MachineryManagerGuard />}>
              <Route path="catalog" element={<CatalogBrowsePage />} />
              <Route path="catalog/:modelId" element={<CatalogModelDetailPage />} />
            </Route>
            <Route path=":id/service-reports/new" element={<CreateServiceReportPage />}></Route>
            <Route path=":id/service-reports/:reportNumber/solve" element={<SolveServiceReportPage />}></Route>
            <Route path=":id/service-reports/:reportNumber" element={<SrDetailPage />}></Route>
            <Route path=":id" element={<Machine />}></Route>
            <Route path="" element={<Machinery />}></Route>
          </Route>
        </Route>

        {/* Operations routes */}
        <Route element={<PermissionRouteGuard module={Modules.Maintenance} />}>
          <Route path="operations" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route index element={<OpsIndexRedirect />} />
            {/* Manager+ sub-sections (Workers redirect to my-tasks) */}
            <Route element={<OpsManagerGuard />}>
              <Route path="service-reports" element={<ServiceReportsPage />} />
              <Route path="work-board" element={<WorkBoardPage />} />
              <Route path="schedule" element={<Navigate to="/operations/work-board?view=calendar" replace />} />
              <Route path="analytics" element={<AnalyticsDashboardPage />} />
              <Route path="work-orders/new" element={<CreateWorkOrderPage />} />
            </Route>
            {/* Available to all (including Workers) */}
            <Route path="my-tasks" element={<MyTasksPage />} />
            <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
          </Route>
        </Route>

        <Route path="search/:productId/:productNumber" element={<ProductSuppliers />}></Route>
        <Route path="search" element={<Search />}></Route>

        <Route element={<PermissionRouteGuard module={Modules.Trading} />}>
          <Route path="trading" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="my-rfqs" element={<MyRfqs />}></Route>
            <Route path="my-rfqs/new" element={<CreateRfq />}></Route>
            <Route path="rfq-feed" element={<RfqFeed />}></Route>
            <Route path="new-offer/:id" element={<CreateOffer />}></Route>
            <Route path="offers-received" element={<OffersReceived />}></Route>
            <Route path="offers-sent" element={<SentOffers />}></Route>
            <Route path="orders" element={<Orders />}></Route>
            <Route path="" element={<Navigate to="my-rfqs" />}></Route>
          </Route>
        </Route>

        <Route element={<PermissionRouteGuard module={Modules.Inventory} />}>
          <Route path="supplier" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="inventory" element={<InventoryTable />}></Route>
            <Route path="inventory/:id" element={<Navigate to="/supplier/inventory" replace />}></Route>
            <Route path="upload/:id" element={<UploadStockItemsFile />}></Route>
            <Route path="new/:groupId" element={<EditStockItem />}></Route>
            <Route path=":id" element={<EditStockItem />}></Route>
            <Route path="" element={<Navigate to="inventory" />}></Route>
          </Route>
        </Route>

        <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
          <Route path="service-requests" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<MyServiceRequests />}></Route>
            <Route path="new" element={<EditServiceRequestPage />}></Route>
            <Route path="edit/:id" element={<EditServiceRequestPage />}></Route>
            <Route path=":id/offers/compare" element={<ServiceRequestCompareRedirect />}></Route>
            <Route path=":id" element={<ServiceRequestRedirect />}></Route>
          </Route>
        </Route>
      </Route>

      {/* Provider-only routes */}
      <Route element={<TypeGuard requiredType="provider" />}>
        <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
          <Route path="service-offers" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<MyOffers />}></Route>
            <Route path="new/:serviceRequestId" element={<EditOfferPage />}></Route>
            <Route path="edit/:id" element={<EditOfferPage />}></Route>
          </Route>
          <Route path="active-work" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
            <Route path="" element={<ActiveWork />}></Route>
          </Route>
        </Route>
      </Route>

      {/* Visible to all types — offer detail accessible by both owner and provider */}
      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-offers/:id" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<OfferDetailPage />}></Route>
        </Route>
      </Route>

      {/* Visible to all types */}
      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-board" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<ServiceBoard />}></Route>
          <Route path=":id/offers/compare" element={<CompareOffersPage />}></Route>
          <Route path=":id" element={<RfsDetailPage />}></Route>
        </Route>
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.Servicing} />}>
        <Route path="service-tracking" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<ServiceTrackingPage />}></Route>
        </Route>
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.PartnerHub} />}>
        <Route path="companies" element={<Companies />}></Route>
        <Route path="companies/:id" element={<Company />}></Route>
        <Route path="partnerships" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<PartnershipsPage />}></Route>
        </Route>
      </Route>

      {/* MIDAS full-screen assistant -- AuthorizedRoute + isBotEnabled gate (SEC-REQ-2) */}
      <Route path="midas" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route element={<MidasRouteGuard />}>
          <Route path="" element={<MidasPage />} />
        </Route>
      </Route>

      {/* MIDAS V2 (Phase 0 — infrastructure-only placeholder).
          Same auth chain as /midas: AuthorizedRoute + MidasRouteGuard preserves SEC-REQ-1. */}
      <Route path="midas-v2" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
        <Route element={<MidasRouteGuard />}>
          <Route path="" element={<MidasPageV2 />} />
        </Route>
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.Settings} />}>
        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<SettingsProfile />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          <Route path="team" element={<SettingsTeam />} />
          <Route path="company" element={<SettingsCompany />} />
          <Route path="pricing" element={<SettingsPricing />} />
          <Route path="service" element={<SettingsService />} />
          <Route path="shipping" element={<SettingsShipping />} />
          <Route path="appearance" element={<SettingsAppearance />} />
          <Route path="locations" element={<LocationsPage />} />
        </Route>
        <Route path="settings/my-account" element={<Navigate to="/settings/profile" replace />} />
      </Route>

      <Route element={<PermissionRouteGuard module={Modules.Notifications} />}>
        <Route path="notifications" element={<AuthorizedRoute role={Roles.ALL_COMPANY_ROLES} />}>
          <Route path="" element={<NotificationHistoryPage />} />
        </Route>
      </Route>

      </Route>{/* end ViewerRouteGuard */}

      <Route path="" element={<Navigate to={defaultRoute()} />} />
      <Route path="*" element={<Navigate to="" />} />
    </Route>
  );
};

export default useCustomerRoutes;
